import {normalizeIdCountry} from '@sollapay/domain';
import {
  SellerType,
  IdentityDocumentType,
  PartyRole,
  RelationshipType,
  TaxResidency
} from '@sollapay/enums';

import type {SellerEntryFormValues} from '../../../validation';
import type {PepPartyFormValues, PepFormValues} from '@/components/pep';
import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {PepPublicRole, PepRelationship} from '@sollapay/enums';
import type {
  PepDeclarationDTO,
  PepPartyDTO,
  TrustSellerDetailsDTO,
  UpsertSellerEntry,
  UpsertSellersPayload
} from '@sollapay/types';

export type MetFaceToFaceValue = 'yes' | 'no' | '';

export type SellerFormValues = SellerEntryFormValues;

export interface SellersFormValues {
  sellers: SellerFormValues[];
}

export const EMPTY_SELLER_FORM_VALUES: SellerFormValues = {
  id: undefined,
  sellerRole: PartyRole.SELLER,
  sellerType: SellerType.CORPORATION,
  relationshipType: '',
  metFaceToFace: '',
  fullName: '',
  idNumber: '',
  idType: IdentityDocumentType.NATIONAL_ID,
  idCountry: '',
  birthOrIncorporationDate: '',
  address: '',
  taxResidency: '' as TaxResidency,
  taxResidencyCountry: '',
  pep: {isPep: false, parties: []}
};

const toMetFaceToFaceValue = (value: boolean | null | undefined): MetFaceToFaceValue => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
};

const fromMetFaceToFaceValue = (value: MetFaceToFaceValue): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};

const toPepPartyFormValues = (party: PepPartyDTO): PepPartyFormValues => ({
  relationship: party.relationship ?? '',
  fullName: party.fullName,
  countryOfOffice: party.countryOfOffice,
  publicRole: party.publicRole ?? '',
  stillInRole: party.stillInRole,
  roleStartMonth: party.roleStartMonth ?? '',
  roleEndMonth: party.roleEndMonth ?? '',
  sourceOfWealth: party.sourceOfWealth
});

export const toSellerFormValues = (seller: TrustSellerDetailsDTO): SellerFormValues => ({
  id: seller.id,
  sellerRole: seller.sellerRole ?? PartyRole.SELLER,
  sellerType:
    seller.sellerType === SellerType.CORPORATION || seller.sellerType === SellerType.INDIVIDUAL
      ? seller.sellerType
      : ('' as SellerType),
  relationshipType:
    seller.relationshipType === RelationshipType.FIRST_PROJECT ||
    seller.relationshipType === RelationshipType.FEW_PROJECTS ||
    seller.relationshipType === RelationshipType.ONGOING_COLLAB
      ? seller.relationshipType
      : '',
  metFaceToFace: toMetFaceToFaceValue(seller.metFaceToFace),
  fullName: seller.fullName ?? '',
  idNumber: seller.idNumber ?? '',
  idType: seller.idType ?? IdentityDocumentType.NATIONAL_ID,
  idCountry: seller.idCountry ?? '',
  birthOrIncorporationDate: seller.birthOrIncorporationDate ?? '',
  address: seller.address ?? '',
  taxResidency:
    seller.taxResidency === TaxResidency.ISRAEL || seller.taxResidency === TaxResidency.OTHER
      ? seller.taxResidency
      : ('' as TaxResidency),
  taxResidencyCountry: seller.taxResidencyCountry ?? '',
  pep: {
    isPep: seller.pep?.isPep ?? false,
    parties: (seller.pep?.parties ?? []).map(toPepPartyFormValues)
  }
});

/** `ensureOneRow` is for single-party trusts, which have no roster to add a seller from. */
export const toSellersFormValues = (
  sellers: TrustSellerDetailsDTO[],
  ensureOneRow = false
): SellersFormValues => ({
  sellers:
    ensureOneRow && sellers.length === 0
      ? [EMPTY_SELLER_FORM_VALUES]
      : sellers.map(toSellerFormValues)
});

const isEmptyPepParty = (p: PepPartyFormValues): boolean =>
  !p.relationship &&
  !p.fullName.trim() &&
  !p.countryOfOffice.trim() &&
  !p.publicRole &&
  !p.sourceOfWealth.trim() &&
  !p.roleStartMonth;

const buildPepPayload = (pep: PepFormValues, sellerFullName?: string): PepDeclarationDTO => {
  if (!pep.isPep) return {isPep: false, parties: []};

  const parties: PepPartyDTO[] = pep.parties
    .filter(p => !isEmptyPepParty(p))
    .map(p => ({
      id: '',
      relationship: (p.relationship as PepRelationship) || null,
      fullName: sellerFullName ?? p.fullName.trim(),
      countryOfOffice: p.countryOfOffice.trim(),
      publicRole: (p.publicRole as PepPublicRole) || null,
      stillInRole: p.stillInRole,
      roleStartMonth: p.roleStartMonth || null,
      roleEndMonth: p.stillInRole ? null : p.roleEndMonth || null,
      sourceOfWealth: p.sourceOfWealth.trim()
    }));

  return {isPep: true, parties};
};

export const toUpsertSellerEntry = (
  values: SellerFormValues,
  strategy: TrustTypeStrategy
): UpsertSellerEntry => {
  const isIndividual = values.sellerType === SellerType.INDIVIDUAL;

  return {
    ...(values.id ? {id: values.id} : {}),
    sellerRole: strategy.resolveSellerRole(values.sellerRole || null),
    sellerType: values.sellerType || null,
    relationshipType: values.relationshipType || null,
    metFaceToFace: fromMetFaceToFaceValue(values.metFaceToFace),
    fullName: values.fullName.trim() || null,
    idNumber: values.idNumber.trim() || null,
    idType: values.idType,
    idCountry: normalizeIdCountry(values.idType, values.idCountry),
    birthOrIncorporationDate: values.birthOrIncorporationDate.trim() || null,
    address: values.address.trim(),
    taxResidency: values.taxResidency || null,
    taxResidencyCountry:
      values.taxResidency === TaxResidency.OTHER ? values.taxResidencyCountry.trim() || null : null,
    pep: buildPepPayload(values.pep, isIndividual ? values.fullName.trim() : undefined)
  };
};

export const toUpsertSellersPayload = (
  values: SellersFormValues,
  strategy: TrustTypeStrategy
): UpsertSellersPayload => ({
  sellers: values.sellers.map(seller => toUpsertSellerEntry(seller, strategy))
});
